import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Upload, FileText, X } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import employeeApi from '../../lib/employeeApi';
import api from '../../lib/api';
import { getEmployeeUser } from '../../lib/employeeAuth';

export default function CompleteProfilePage() {
  const navigate = useNavigate();
  const employee = getEmployeeUser();
  const [loading, setLoading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    mobile_number: '',
    personal_email: '',
    aadhar_number: '',
    aadhar_file_id: '',
    aadhar_file_name: '',
    pan_number: '',
    pan_file_id: '',
    pan_file_name: '',
    bank_account_holder: '',
    bank_ifsc: '',
    bank_account_number: '',
    permanent_address: '',
    local_address: '',
    local_guardian_name: '',
    local_guardian_contact: '',
  });

  const handleFileUpload = async (file, docType) => {
    setUploadingDoc(docType);
    const fileFormData = new FormData();
    fileFormData.append('file', file);
    try {
      const response = await employeeApi.post('/api/files/upload', fileFormData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (docType === 'aadhar') {
        setFormData({ ...formData, aadhar_file_id: response.data._id, aadhar_file_name: response.data.filename });
      } else if (docType === 'pan') {
        setFormData({ ...formData, pan_file_id: response.data._id, pan_file_name: response.data.filename });
      }
      toast.success(`${docType} uploaded successfully`);
    } catch (error) {
      toast.error(`Failed to upload ${docType}`);
    } finally {
      setUploadingDoc(null);
    }
  };

  const aadharDropzone = useDropzone({
    accept: { 'image/*': ['.png', '.jpg', '.jpeg'], 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    maxSize: 5242880,
    onDrop: async (files) => { if (files.length > 0) await handleFileUpload(files[0], 'aadhar'); }
  });

  const panDropzone = useDropzone({
    accept: { 'image/*': ['.png', '.jpg', '.jpeg'], 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    maxSize: 5242880,
    onDrop: async (files) => { if (files.length > 0) await handleFileUpload(files[0], 'pan'); }
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.aadhar_file_id || !formData.pan_file_id) {
      toast.error('Please upload Aadhar and PAN cards');
      return;
    }
    setLoading(true);
    try {
      await employeeApi.put('/api/employee/profile/complete', formData);
      toast.success('Profile completed successfully!');
      navigate('/employee/dashboard');
    } catch (error) {
      toast.error('Failed to complete profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white/40 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-soft border p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-navy-900 mb-2">Complete Your Profile</h1>
            <p className="text-neutral-600">Please fill in the following mandatory details to continue</p>
            <p className="text-sm text-neutral-500 mt-2">Work Email: {employee?.work_email}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Personal Info */}
            <div>
              <h2 className="text-xl font-semibold text-navy-800 mb-4">Personal Information</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><label className="block text-sm font-medium mb-2">Full Name *</label><input type="text" required value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} className="w-full px-4 py-2 border rounded-xl" data-testid="fullname-input" /></div>
                <div><label className="block text-sm font-medium mb-2">Mobile Number *</label><input type="tel" required value={formData.mobile_number} onChange={(e) => setFormData({ ...formData, mobile_number: e.target.value })} className="w-full px-4 py-2 border rounded-xl" data-testid="mobile-input" /></div>
                <div><label className="block text-sm font-medium mb-2">Personal Email *</label><input type="email" required value={formData.personal_email} onChange={(e) => setFormData({ ...formData, personal_email: e.target.value })} className="w-full px-4 py-2 border rounded-xl" data-testid="personal-email-input" /></div>
                <div className="col-span-2"><label className="block text-sm font-medium mb-2">Work Email (Pre-filled)</label><input type="email" value={employee?.work_email} readOnly className="w-full px-4 py-2 border rounded-xl bg-white/40" /></div>
              </div>
            </div>

            {/* Documents */}
            <div>
              <h2 className="text-xl font-semibold text-navy-800 mb-4">Identity Documents</h2>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Aadhar Card *</label>
                  {!formData.aadhar_file_id ? (
                    <div {...aadharDropzone.getRootProps()} className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer ${aadharDropzone.isDragActive ? 'border-purple-500 bg-purple-50' : 'border-white/40'}`}>
                      <input {...aadharDropzone.getInputProps()} />
                      {uploadingDoc === 'aadhar' ? <Loader2 className="w-8 h-8 text-purple-500 animate-spin mx-auto" /> : <><Upload className="w-8 h-8 text-neutral-400 mx-auto mb-2" /><p className="text-sm text-neutral-600">Upload Aadhar</p></>}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                      <div className="flex items-center gap-2"><FileText className="w-6 h-6 text-emerald-600" /><span className="text-sm">{formData.aadhar_file_name}</span></div>
                      <button type="button" onClick={() => setFormData({ ...formData, aadhar_file_id: '', aadhar_file_name: '' })} className="text-red-600"><X className="w-4 h-4" /></button>
                    </div>
                  )}
                  <input type="text" required value={formData.aadhar_number} onChange={(e) => setFormData({ ...formData, aadhar_number: e.target.value })} placeholder="Aadhar Number" className="w-full px-4 py-2 border rounded-xl mt-2" data-testid="aadhar-number-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">PAN Card *</label>
                  {!formData.pan_file_id ? (
                    <div {...panDropzone.getRootProps()} className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer ${panDropzone.isDragActive ? 'border-purple-500 bg-purple-50' : 'border-white/40'}`}>
                      <input {...panDropzone.getInputProps()} />
                      {uploadingDoc === 'pan' ? <Loader2 className="w-8 h-8 text-purple-500 animate-spin mx-auto" /> : <><Upload className="w-8 h-8 text-neutral-400 mx-auto mb-2" /><p className="text-sm text-neutral-600">Upload PAN</p></>}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                      <div className="flex items-center gap-2"><FileText className="w-6 h-6 text-emerald-600" /><span className="text-sm">{formData.pan_file_name}</span></div>
                      <button type="button" onClick={() => setFormData({ ...formData, pan_file_id: '', pan_file_name: '' })} className="text-red-600"><X className="w-4 h-4" /></button>
                    </div>
                  )}
                  <input type="text" required value={formData.pan_number} onChange={(e) => setFormData({ ...formData, pan_number: e.target.value })} placeholder="PAN Number" className="w-full px-4 py-2 border rounded-xl mt-2" data-testid="pan-number-input" />
                </div>
              </div>
            </div>

            {/* Bank Details */}
            <div>
              <h2 className="text-xl font-semibold text-navy-800 mb-4">Bank Account Details</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><label className="block text-sm font-medium mb-2">Account Holder Name *</label><input type="text" required value={formData.bank_account_holder} onChange={(e) => setFormData({ ...formData, bank_account_holder: e.target.value })} className="w-full px-4 py-2 border rounded-xl" data-testid="bank-holder-input" /></div>
                <div><label className="block text-sm font-medium mb-2">IFSC Code *</label><input type="text" required value={formData.bank_ifsc} onChange={(e) => setFormData({ ...formData, bank_ifsc: e.target.value })} className="w-full px-4 py-2 border rounded-xl" data-testid="bank-ifsc-input" /></div>
                <div><label className="block text-sm font-medium mb-2">Account Number *</label><input type="text" required value={formData.bank_account_number} onChange={(e) => setFormData({ ...formData, bank_account_number: e.target.value })} className="w-full px-4 py-2 border rounded-xl" data-testid="bank-account-input" /></div>
              </div>
            </div>

            {/* Addresses */}
            <div>
              <h2 className="text-xl font-semibold text-navy-800 mb-4">Address Details</h2>
              <div className="space-y-4">
                <div><label className="block text-sm font-medium mb-2">Permanent Address *</label><textarea required value={formData.permanent_address} onChange={(e) => setFormData({ ...formData, permanent_address: e.target.value })} rows={3} className="w-full px-4 py-2 border rounded-xl" data-testid="permanent-address-input" /></div>
                <div><label className="block text-sm font-medium mb-2">Local Address *</label><textarea required value={formData.local_address} onChange={(e) => setFormData({ ...formData, local_address: e.target.value })} rows={3} className="w-full px-4 py-2 border rounded-xl" data-testid="local-address-input" /></div>
              </div>
            </div>

            {/* Local Guardian */}
            <div>
              <h2 className="text-xl font-semibold text-navy-800 mb-4">Local Guardian Details</h2>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium mb-2">Guardian Name *</label><input type="text" required value={formData.local_guardian_name} onChange={(e) => setFormData({ ...formData, local_guardian_name: e.target.value })} className="w-full px-4 py-2 border rounded-xl" data-testid="guardian-name-input" /></div>
                <div><label className="block text-sm font-medium mb-2">Guardian Contact *</label><input type="tel" required value={formData.local_guardian_contact} onChange={(e) => setFormData({ ...formData, local_guardian_contact: e.target.value })} className="w-full px-4 py-2 border rounded-xl" data-testid="guardian-contact-input" /></div>
              </div>
            </div>

            <button type="submit" disabled={loading} className="w-full px-8 py-3 bg-purple-500 text-white rounded-xl hover:bg-purple-600 font-medium disabled:opacity-50 flex items-center justify-center" data-testid="complete-profile-button">
              {loading ? <><Loader2 className="animate-spin mr-2 w-5 h-5" />Submitting...</> : 'Complete Profile'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}